import type { ReviewFlavor } from './review-flavors.js';
import type { BrowserEngine } from './browser-engine.js';
import type { BrowserViewportPreset } from './browser-viewport.js';

export const ADHOC_TEST_JOB_TYPE = 'adhoc.test' as const;
/**
 * The completion waiter a chat-started (Slack/Teams) ad-hoc test parks beside
 * the generic `adhoc.test-complete` one. Lives here so `cancelAdhocTest` can
 * name it without importing the chat job module (and its agent, resolver and
 * job-registration side effects).
 */
export const CHAT_ADHOC_TEST_COMPLETE_JOB_TYPE = 'chat.adhoc-test-complete' as const;
export const ADHOC_TEST_NAMING_JOB_TYPE = 'adhoc.test.naming' as const;
export const ADHOC_TEST_INSTRUCTIONS_MAX_LENGTH = 5000;

export type AdhocTestPayload = {
  adhocTestId: string;
  propertyId: string;
  credentialId?: string | null;
  /** Live property environment selected at run start. Null falls back to credential/default env. */
  environmentId?: string | null;
  instructions: string;
  startUrl?: string | null;
  /**
   * Review flavor. Absent/`functional` runs the existing pass/fail QA executor;
   * `ux` (and future graded-critique flavors) dispatch to the review executor.
   */
  flavor?: ReviewFlavor;
  /** Concrete viewport preset resolved when the run was created. */
  viewportSize: BrowserViewportPreset;
  /** Concrete Playwright engine resolved when the run was created. */
  browserEngine: BrowserEngine;
  /**
   * UX flavor: mint a throwaway email+password identity (with a live inbox) so
   * the agent can sign up / onboard itself. The run still starts logged out.
   */
  provisionTempIdentity?: boolean;
  explorationAssignmentId?: string;
};

/**
 * Payload for the ad-hoc test naming job.
 * Generates an AI-powered label for an exploratory test (or the legacy run
 * title when there is no parent).
 */
export type AdhocTestNamingPayload = {
  adhocTestId: string;
  propertyName: string;
  instructions: string;
  /**
   * Parent exploratory test to name. When set, the generated label is written
   * to `exploratoryTests.name`. When null (PR/ticket runs), the label falls
   * back to the legacy `adhocTests.title`.
   */
  exploratoryTestId?: string | null;
};
